import {
  Box,
  Button,
  CardActions,
  CircularProgress,
  createMuiTheme,
  Divider,
  makeStyles,
  MenuItem,
  Paper,
  Popover,
  Portal,
  Select,
  Snackbar,
  Theme,
  ThemeProvider,
  Tooltip,
  Typography,
  Zoom,
} from "@material-ui/core";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { useDispatch, useSelector } from "react-redux";
import { FiboType, Label, Finish, LabelOption } from "../types";

import { ReactComponent as BigArrowsCenterText } from "../svg/big-arrows-center-text.svg";
import { ReactComponent as BigArrowsSideText } from "../svg/big-arrows-side-text.svg";
import { ReactComponent as BigSideTextOnly } from "../svg/big-side-text-only.svg";
import { ReactComponent as BigSymbolsArrows } from "../svg/big-symbols-arrows.svg";
import { ReactComponent as BigSymbolsNoArows } from "../svg/big-symbols-no-arrows.svg";
import { ReactComponent as BigSymbolsCenterText } from "../svg/big-symbols-center-text.svg";
import { ReactComponent as BigArrowsCenterSymbol } from "../svg/big-arrows-center-symbol.svg";

import { ReactComponent as SmallArrowsCenterText } from "../svg/small-arrows-center-text.svg";
import { ReactComponent as SmallArrowsSideText } from "../svg/small-arrows-side-text.svg";
import { ReactComponent as SmallSideTextOnly } from "../svg/small-side-text-only.svg";
import { ReactComponent as SmallSymbolsArrows } from "../svg/small-symbols-arrows.svg";
import { ReactComponent as SmallSymbolsNoArows } from "../svg/small-symbols-no-arrows.svg";
import { ReactComponent as SmallSymbolsCenterText } from "../svg/small-symbols-center-text.svg";
import { ReactComponent as SmallArrowsCenterSymbol } from "../svg/small-arrows-center-symbol.svg";

import { ReactComponent as ArrowUpSvg } from "../svg/arrow-up.svg";
import { ReactComponent as ArrowDownSvg } from "../svg/arrow-down.svg";

import EditIcon from "@material-ui/icons/Edit";

import { RootState } from "./store";
import fibo, { fiboSelectors } from "./store/fibo";

import { iconUrl } from "./store/icons";

import { from } from "rxjs";

import blobStream from "blob-stream";
import fs from "fs";
import * as paper from "paper";

// eslint-disable-next-line import/no-webpack-loader-syntax
import SVGWorker from "worker-loader!./svg.worker";
import { DebouncedTextField, IconSelect } from "./components/LabelInput";
import { useAdvanced } from "./hooks";
import { Alert } from "@material-ui/lab";

// Create 4 webworkers
const workerPool = [0, 0, 0, 0].map((_) => new SVGWorker());

let messageId = 0;

function requestFromWorker<T>(funcName: string, ...params: any[]) {
  const id = messageId++;
  const worker = workerPool[id % workerPool.length];
  return new Promise<T>((resolve, reject) => {
    const handler = (ev: MessageEvent<any>) => {
      if (ev.data.id !== id) return;
      worker.removeEventListener("message", handler);
      if (ev.data.result) return resolve(ev.data.result);
      reject(ev.data);
    };
    worker.addEventListener("message", handler);
    worker.postMessage({
      id,
      funcName,
      params: params,
    });
  });
}

const generateText = (
  text: string,
  fontSize: number,
  textAnchor: string,
  x: number,
  y: number
) => {
  return requestFromWorker<{ pathData: string; width: number }>(
    "generateText",
    text,
    fontSize,
    textAnchor,
    x,
    y
  ).then(({ pathData, width }) => {
    const cacheKey = `${text}-${fontSize}`;
    if (textWidthRequests[cacheKey]) textWidthRequests[cacheKey](width);
    textWidths[cacheKey] = width;

    return pathData;
  });
};

// This is pretty inelegant, but it works, for now
const textWidths: {
  [id: string]: number;
} = {};
const textWidthRequests: {
  [id: string]: (res: number) => void;
} = {};
export const getTextWidth = async (text: string, fontSize: number) => {
  const cacheKey = `${text}-${fontSize}`;

  if (textWidths[cacheKey]) return textWidths[cacheKey];

  return new Promise<number>((resolve, reject) => {
    textWidthRequests[cacheKey] = resolve;
  });
};

const pathDataCache: { [id: string]: Promise<string> } = {};
const getIconPathData = async (id: string, strokeWidth: number) => {
  const cacheKey = `icon-${strokeWidth}-${id}`;

  if (pathDataCache[cacheKey]) return pathDataCache[cacheKey];

  const parsedSvg = await new Promise<paper.Project>(
    async (resolve, reject) => {
      // TODO: figure out how to make this work in the webworker
      const proj = new paper.Project(new paper.Size(1, 1));

      // Don't ask me why this is needed, but it works
      proj.importSVG(iconUrl(id) + "?hack", {
        expandShapes: true,
        onError: reject,
        onLoad: () => {
          resolve(proj);
        },
      });
    }
  ).then((proj) => {
    const findAllPaths = (child: paper.Item): paper.PathItem[] => {
      if (child instanceof paper.PathItem) return [child];

      return child.children?.flatMap(findAllPaths) ?? [];
    };
    const paths = proj.activeLayer.children.flatMap(findAllPaths);

    return {
      pathDatas: paths.map((path) => path.pathData),
      bounds: proj.activeLayer.bounds,
    };
  });

  const promise = requestFromWorker<string>(
    "getIconPathData",
    id,
    parsedSvg.pathDatas,
    parsedSvg.bounds.width,
    strokeWidth
  );
  pathDataCache[cacheKey] = promise;

  return promise;
};

const useStyles = makeStyles((theme) => ({
  svg: {
    maxHeight: "60vh",
    height: "100%",
    width: "100%",
    fontFamily: "HelveticaNeue-Light",
    letterSpacing: 0,
    // border: "1px solid #ccc",
    overflow: "visible", // to make outline visible
    //boxShadow: "3px 3px 5px 2px rgba(0,0,0,0.4)",
    filter: "drop-shadow(3px 3px 5px rgba(0, 0, 0, .5))",
  },
  svgClickableArea: {
    cursor: "pointer",
    "& rect:hover": {
      fill: "#222",
      stroke: theme.palette.primary.dark,
      strokeWidth: "0.7",
    },
  },
  invert: {
    filter: "invert()",
    backgroundColor: "white",
    padding: "0 10px",
  },
  svgIcon: {
    filter: "invert()",
    width: "32px",
    height: "32px",
    minWidth: "32px",
    minHeight: "32px",
  },
  leftInput: {
    "& input": {
      textAlign: "left",
    },
  },
  centerInput: {
    "& input": {
      textAlign: "center",
    },
  },
  rightInput: {
    "& input": {
      textAlign: "right",
    },
  },
  denseSelect: {
    padding: "5px",
  },
  labelVariantElement: {
    width: "100%",
    height: "30px",
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    "& svg": {
      height: "100%",
    },
  },
  backdrop: {
    zIndex: 5,
    backgroundColor: "rgba(0,0,0,0.3)",
  },
  alignRight: {
    marginLeft: "auto",
  },
}));

export const getRenderedComponent = async (
  children: React.ReactElement<any>,
  exportMode = true
) => {
  const promisesToAwait: Promise<any>[] = [];
  const pathDatas: { [id: string]: string } = {};

  ReactDOMServer.renderToStaticMarkup({
    ...children,
    props: {
      ...children.props,
      exportMode: true,
      showPopover: false,
      registerPathData: (id: string, p: Promise<string>) => {
        promisesToAwait.push(
          p.then((path) => {
            pathDatas[id] = path;
          })
        );
      },
    },
  });

  await Promise.all(promisesToAwait);

  const component: React.ReactElement = {
    ...children,
    props: {
      ...children.props,
      exportMode,
      getPathData: (id: string) => pathDatas[id] ?? "",
    },
  };

  return exportMode
    ? ReactDOMServer.renderToStaticMarkup(component)
    : component;
};

export const exportLabelComponent = async (
  children: React.ReactElement<{
    x: number;
    y: number;
    width: number;
    height: number;

    // Export to PDF stuff
    registerPathData?: (id: string, p: Promise<string>) => void;
    getPathData?: (id: string) => string;
  }>
) => {
  const circleOffset = 5;

  // Some funky stuff to make the laser program size/scale the pdf correctly
  // https://github.com/bpampuch/pdfmake/issues/334#issuecomment-195306315
  const scalingFactor = 2.54;
  const toExport = `
  <g transform="scale(${scalingFactor})">
    <g transform="scale(-1, 1) translate(-${
      children.props.width + circleOffset * 2
    },0)">
      <g>
        <circle cx="0" cy="0" r="0.1" fill="red" />
        <circle cx="${children.props.width + circleOffset * 2}" cy="${
    children.props.height + circleOffset * 2
  }" r="0.1" fill="red" />
      </g>
      <g transform="translate(${circleOffset}, ${circleOffset})">
        ${await getRenderedComponent(children)}
      </g>
    </g>
  </g>
  `;

  fs.writeFileSync(
    "data/Helvetica.afm",
    (await import("!!raw-loader!pdfkit/js/data/Helvetica.afm")).default
  );
  const doc = new (await import("pdfkit")).default({
    size: [
      (children.props.width + circleOffset * 2) * scalingFactor,
      (children.props.height + circleOffset * 2) * scalingFactor,
    ],
    margin: 0,
  });
  const stream = doc.pipe(blobStream());
  (await import("svg-to-pdfkit")).default(doc, toExport, 0, 0, {
    assumePt: true,
  });
  doc.end();

  return new Promise<Blob>((resolve, reject) => {
    stream.on("finish", () => {
      const blob = stream.toBlob("application/pdf");
      resolve(blob);
    });
  });
};

const iconYOffset = (iconSize: number) => (7 - iconSize) / 2;

interface IconProps {
  x: number;
  y: number;
  size: number;
  id: string;
  strokeWidth: number;

  registerPathData?: (id: string, p: Promise<string>) => void;
  getPathData?: (id: string) => string;
}
function Icon({
  x,
  y,
  size,
  id,
  strokeWidth,
  registerPathData,
  getPathData,
}: IconProps) {
  // Do not draw "no icon"
  if (!id || id === "") return <></>;

  ////////////////////////////
  // RENDERING TO PDF START //

  const renderId = `icon-${strokeWidth}-${id}`;

  const iconSvg = (pathData: string) => (
    <svg
      fillRule="evenodd"
      stroke="none"
      height={size}
      width={size}
      x={x}
      y={y}
      viewBox="0 0 28.35 28.35"
    >
      <path d={pathData}></path>
    </svg>
  );

  if (registerPathData)
    registerPathData(renderId, getIconPathData(id, strokeWidth));

  // If we receive getPathData as a prop, render immediately
  if (getPathData) return iconSvg(getPathData(renderId));

  // RENDERING TO PDF END //
  //////////////////////////

  return (
    <svg>
      <defs>
        <filter id="invert">
          <feColorMatrix
            type="matrix"
            values="1 0 0 0 255
                    0 1 0 0 255
                    0 0 1 0 255
                    0 0 0 1 0"
          />
        </filter>
      </defs>
      <image
        x={x}
        y={y}
        href={iconUrl(id)}
        width={size}
        height={size}
        filter="url(#invert)"
      />
    </svg>
  );
}

interface TextProps {
  x: number;
  y: number;
  fontSize: number;
  textAnchor: string;
  children?: string;

  registerPathData?: (id: string, p: Promise<string>) => void;
  getPathData?: (id: string) => string;
}
function Text({
  children,
  fontSize,
  textAnchor,
  x,
  y,

  // Rendering to PDF
  registerPathData,
  getPathData,
}: TextProps) {
  ////////////////////////////
  // RENDERING TO PDF START //

  const id = `text-${children}-${fontSize}-${textAnchor}-${x}-${y}`;

  if (children && registerPathData)
    registerPathData(id, generateText(children, fontSize, textAnchor, x, y));

  // If we receive getPathData as a prop, render immediately
  if (getPathData)
    return (
      <g transform="translate(0, 1.5)">
        <path d={getPathData(id)} fillRule="evenodd"></path>
      </g>
    );

  // RENDERING TO PDF END //
  //////////////////////////

  return (
    <g transform="translate(0, 1.5)">
      <text x={x} y={y} fontSize={fontSize} textAnchor={textAnchor}>
        {children}
      </text>
    </g>
  );
}

export const LabelInteractions = ({
  id,
  labelIndex,
  children,
}: {
  id: string;
  labelIndex: number;
  children: React.ReactElement;
}) => {
  const [snackbarInvalidOpen, setSnackbarInvalidOpen] = React.useState(false);
  const [popoverPos, setPopoverPos] = useState<{ x: number; y: number } | null>(
    null
  );

  const g = useRef<SVGGElement | null>(null);

  const classes = useStyles();
  const dispatch = useDispatch();
  const currentLabel = useSelector(
    (state: RootState) => fiboSelectors.selectById(state, id)?.label[labelIndex]
  );
  const advanced = useSelector((state: RootState) => state.document.advanced);

  const { veryAdvanced, advanced: advancedMode } = useAdvanced();

  const icons = useSelector((state: RootState) => [
    ...state.icons.entities.filter(
      (icon) => !icon.hideForFibo && (veryAdvanced || !icon.advancedOnly)
    ),
  ]);

  if (!currentLabel) return <></>;

  const labelUpdate = async (labelIndex: number, label: Partial<Label>) => {
    dispatch(
      fibo.actions.labelUpdated({
        id,
        labelIndex,
        label,
      })
    );

    const potentialLabel = { ...currentLabel, ...label };

    const { leftText, centerText, rightText } = potentialLabel;
    const textGenP = [leftText, centerText, rightText]
      .filter((text) => !!text.length)
      .map((text) => getTextWidth(text, advanced.fontSize));

    const maxWidth = labelIndex === 1 ? 24 : 35;
    let width = (await Promise.all(textGenP)).reduce(
      (acc, res) => acc + res,
      0
    );
    if (hasArrows(potentialLabel.labelType))
      if (potentialLabel.centerText) width += advanced.textOffsetFromIcon * 1.2;
      else width += 2 * (advanced.textOffsetFromIcon * 0.8);

    if (width > maxWidth) {
      setSnackbarInvalidOpen(true);
      dispatch(
        fibo.actions.labelUpdated({
          id,
          labelIndex,
          label: currentLabel,
        })
      );
    }
  };

  const onLabelTypeChange = (labelType: LabelOption) => {
    labelUpdate(labelIndex, {
      labelType,
      centerText:
        labelType === LabelOption.ARROWS_CENTER_TEXT
          ? currentLabel.centerText
          : "",
      leftText:
        labelType === LabelOption.ARROWS_SIDE_TEXT ||
        labelType === LabelOption.SIDE_TEXT
          ? currentLabel.leftText
          : "",
      rightText:
        labelType === LabelOption.ARROWS_SIDE_TEXT ||
        labelType === LabelOption.SIDE_TEXT
          ? currentLabel.rightText
          : "",

      leftIconId:
        labelType === LabelOption.ARROWS_SYMBOLS_NO_TEXT ||
        labelType === LabelOption.SYMBOLS_NO_TEXT
          ? currentLabel.leftIconId || "21f17de535c0946f47a0526a1a43d397"
          : "",
      rightIconId:
        labelType === LabelOption.ARROWS_SYMBOLS_NO_TEXT ||
        labelType === LabelOption.SYMBOLS_NO_TEXT
          ? currentLabel.rightIconId || "e0c24cc92775036b023e9c1f2aa5a07e"
          : "",
      centerIconId:
        labelType === LabelOption.ARROWS_CENTER_SYMBOL
          ? currentLabel.centerIconId || "21f17de535c0946f47a0526a1a43d397"
          : "",
    });
  };

  const onTextChange = (pos: "left" | "center" | "right", newText: string) => {
    labelUpdate(labelIndex, {
      [pos + "Text"]: newText,
    });
  };

  const onIconChange = (pos: "left" | "right" | "center", iconId: string) => {
    labelUpdate(labelIndex, {
      [pos + "IconId"]: iconId,
    });
  };

  const bigLabelVariants = [
    {
      id: LabelOption.SIDE_TEXT,
      element: (
        <Box className={classes.labelVariantElement}>
          <BigSideTextOnly />
        </Box>
      ),
    },
    {
      id: LabelOption.ARROWS_SIDE_TEXT,
      element: (
        <Box className={classes.labelVariantElement}>
          <BigArrowsSideText />
        </Box>
      ),
    },
    {
      id: LabelOption.ARROWS_CENTER_TEXT,
      element: (
        <Box className={classes.labelVariantElement}>
          <BigArrowsCenterText />
        </Box>
      ),
    },
    {
      id: LabelOption.SYMBOLS_NO_TEXT,
      element: (
        <Box className={classes.labelVariantElement}>
          <BigSymbolsNoArows />
        </Box>
      ),
    },
    {
      id: LabelOption.ARROWS_SYMBOLS_NO_TEXT,
      element: (
        <Box className={classes.labelVariantElement}>
          <BigSymbolsArrows />
        </Box>
      ),
    },
    {
      id: LabelOption.ARROWS_CENTER_SYMBOL,
      element: (
        <Box className={classes.labelVariantElement}>
          <BigArrowsCenterSymbol />
        </Box>
      ),
    },
  ];

  const smallLabelVariants = [
    {
      id: LabelOption.SIDE_TEXT,
      element: (
        <Box className={classes.labelVariantElement}>
          <SmallSideTextOnly />
        </Box>
      ),
    },
    {
      id: LabelOption.ARROWS_SIDE_TEXT,
      element: (
        <Box className={classes.labelVariantElement}>
          <SmallArrowsSideText />
        </Box>
      ),
    },
    {
      id: LabelOption.ARROWS_CENTER_TEXT,
      element: (
        <Box className={classes.labelVariantElement}>
          <SmallArrowsCenterText />
        </Box>
      ),
    },
    {
      id: LabelOption.SYMBOLS_NO_TEXT,
      element: (
        <Box className={classes.labelVariantElement}>
          <SmallSymbolsNoArows />
        </Box>
      ),
    },
    {
      id: LabelOption.ARROWS_SYMBOLS_NO_TEXT,
      element: (
        <Box className={classes.labelVariantElement}>
          <SmallSymbolsArrows />
        </Box>
      ),
    },
    {
      id: LabelOption.ARROWS_CENTER_SYMBOL,
      element: (
        <Box className={classes.labelVariantElement}>
          <SmallArrowsCenterSymbol />
        </Box>
      ),
    },
  ];

  if (advancedMode) {
    bigLabelVariants.push({
      id: LabelOption.SYMBOLS_CENTER_TEXT,
      element: (
        <Box className={classes.labelVariantElement}>
          <BigSymbolsCenterText />
        </Box>
      ),
    });
    smallLabelVariants.push({
      id: LabelOption.SYMBOLS_CENTER_TEXT,
      element: (
        <Box className={classes.labelVariantElement}>
          <SmallSymbolsCenterText />
        </Box>
      ),
    });
  }

  const labelVariants = [
    { id: LabelOption.UNDEFINED, element: "Please select a variant" },
    ...(labelIndex === 0 ? bigLabelVariants : smallLabelVariants),
  ];

  return (
    <>
      <Tooltip
        title={
          <Box display="flex" alignItems="center">
            <EditIcon fontSize="small" /> <Box ml={1}>Edit Label</Box>
          </Box>
        }
        placement="top"
      >
        <g
          ref={g}
          onClick={(ev) => {
            const rect = (ev.target as SVGGElement).getBoundingClientRect();
            setPopoverPos({
              x: rect.x + rect.width / 2,
              y: rect.y - 3,
            });
          }}
          className={classes.svgClickableArea}
        >
          {children}
          {currentLabel.labelType === LabelOption.UNDEFINED && (
            <text
              textAnchor="middle"
              dominantBaseline="central"
              fontSize="3.35"
              pointerEvents="none"
              transform={(children.props as any).transform}
              x={(children.props as any).width / 2}
              y={(children.props as any).height / 2}
              fill="white"
            >
              click here to edit...
            </text>
          )}
        </g>
      </Tooltip>
      <Popover
        TransitionComponent={Zoom}
        open={!!popoverPos}
        onClose={() => {
          setPopoverPos(null);
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        anchorReference="anchorPosition"
        anchorPosition={{
          left: popoverPos?.x ?? 0,
          top: popoverPos?.y ?? 0,
        }}
        BackdropProps={{
          className: classes.backdrop,
        }}
      >
        <Box maxWidth="400px" width="100%">
          <CardActions disableSpacing>
            <Box mr={3} ml={1} minWidth="120px">
              <Typography variant="body2">Label variant:</Typography>
            </Box>
            <Select
              fullWidth
              classes={{ root: classes.denseSelect }}
              value={currentLabel.labelType}
              onChange={(ev) =>
                onLabelTypeChange(ev.target.value as LabelOption)
              }
              renderValue={(val) =>
                labelVariants.find((v) => v.id === val)?.element
              }
            >
              {labelVariants
                .filter((i) => i.id !== LabelOption.UNDEFINED)
                .map((variant) => (
                  <MenuItem key={variant.id} value={variant.id}>
                    {variant.element}
                  </MenuItem>
                ))}
            </Select>
          </CardActions>
          {currentLabel.labelType !== LabelOption.UNDEFINED && (
            <>
              <Divider />
              <Box width="100%" bgcolor="black" color="white !important">
                <ThemeProvider
                  theme={(theme: Theme) =>
                    createMuiTheme({
                      palette: {
                        primary: theme.palette.primary,
                        type: "dark",
                      },
                    })
                  }
                >
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="64px"
                  >
                    <Box p={1} width="300px">
                      {
                        {
                          [LabelOption.UNDEFINED]: <></>,
                          [LabelOption.ARROWS_CENTER_TEXT]: (
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <ArrowUpSvg className={classes.svgIcon} />
                              <DebouncedTextField
                                className={classes.centerInput}
                                value={currentLabel.centerText}
                                onChange={(ev) =>
                                  onTextChange("center", ev.target.value)
                                }
                                variant="outlined"
                                size="small"
                                placeholder="Enter text here..."
                              />
                              <ArrowDownSvg className={classes.svgIcon} />
                            </Box>
                          ),
                          [LabelOption.ARROWS_SIDE_TEXT]: (
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <ArrowUpSvg className={classes.svgIcon} />
                              <DebouncedTextField
                                className={classes.leftInput}
                                value={currentLabel.leftText}
                                onChange={(ev) =>
                                  onTextChange("left", ev.target.value)
                                }
                                variant="outlined"
                                size="small"
                                placeholder="Left text..."
                              />
                              <DebouncedTextField
                                className={classes.rightInput}
                                value={currentLabel.rightText}
                                onChange={(ev) =>
                                  onTextChange("right", ev.target.value)
                                }
                                variant="outlined"
                                size="small"
                                placeholder="Right text..."
                              />
                              <ArrowDownSvg className={classes.svgIcon} />
                            </Box>
                          ),
                          [LabelOption.ARROWS_SYMBOLS_NO_TEXT]: (
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <ArrowUpSvg className={classes.svgIcon} />
                              <IconSelect
                                icons={icons}
                                currentIconId={currentLabel.leftIconId}
                                onIconSelect={(id) => onIconChange("left", id)}
                              />
                              <Box flexGrow="1"></Box>
                              <IconSelect
                                icons={icons}
                                currentIconId={currentLabel.rightIconId}
                                onIconSelect={(id) => onIconChange("right", id)}
                              />
                              <ArrowDownSvg className={classes.svgIcon} />
                            </Box>
                          ),
                          [LabelOption.SIDE_TEXT]: (
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <DebouncedTextField
                                className={classes.leftInput}
                                value={currentLabel.leftText}
                                onChange={(ev) =>
                                  onTextChange("left", ev.target.value)
                                }
                                variant="outlined"
                                size="small"
                                placeholder="Left text..."
                              />
                              <DebouncedTextField
                                className={classes.rightInput}
                                value={currentLabel.rightText}
                                onChange={(ev) =>
                                  onTextChange("right", ev.target.value)
                                }
                                variant="outlined"
                                size="small"
                                placeholder="Right text..."
                              />
                            </Box>
                          ),
                          [LabelOption.SYMBOLS_NO_TEXT]: (
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <IconSelect
                                icons={icons}
                                currentIconId={currentLabel.leftIconId}
                                onIconSelect={(id) => onIconChange("left", id)}
                              />
                              <Box flexGrow="1"></Box>
                              <IconSelect
                                icons={icons}
                                currentIconId={currentLabel.rightIconId}
                                onIconSelect={(id) => onIconChange("right", id)}
                              />
                            </Box>
                          ),
                          [LabelOption.SYMBOLS_CENTER_TEXT]: (
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <IconSelect
                                icons={icons}
                                currentIconId={currentLabel.leftIconId}
                                onIconSelect={(id) => onIconChange("left", id)}
                              />
                              <DebouncedTextField
                                className={classes.centerInput}
                                value={currentLabel.centerText}
                                onChange={(ev) =>
                                  onTextChange("center", ev.target.value)
                                }
                                variant="outlined"
                                size="small"
                                placeholder="Enter text here..."
                              />
                              <IconSelect
                                icons={icons}
                                currentIconId={currentLabel.rightIconId}
                                onIconSelect={(id) => onIconChange("right", id)}
                              />
                            </Box>
                          ),
                          [LabelOption.ARROWS_CENTER_SYMBOL]: (
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <ArrowUpSvg className={classes.svgIcon} />
                              <Box flexGrow="1"></Box>
                              <IconSelect
                                icons={icons}
                                currentIconId={currentLabel.centerIconId}
                                onIconSelect={(id) =>
                                  onIconChange("center", id)
                                }
                              />
                              <Box flexGrow="1"></Box>
                              <ArrowDownSvg className={classes.svgIcon} />
                            </Box>
                          ),
                        }[currentLabel.labelType]
                      }
                    </Box>
                  </Box>
                </ThemeProvider>
              </Box>
            </>
          )}
          <Divider />
          <CardActions>
            <Button
              className={classes.alignRight}
              color="primary"
              onClick={() => {
                setPopoverPos(null);
              }}
            >
              Done
            </Button>
          </CardActions>
        </Box>
        {/*
  <IconButton
    onClick={async () => {
      setExporting(true);
      await exportLabelComponent(children)
        .then((blob: Blob) => {
          FileSaver.saveAs(blob, `${description}.pdf`);
        })
        .catch(alert);
      setExporting(false);
    }}
    onMouseEnter={() => {
      if (hidePopoverTimer.current)
        clearTimeout(hidePopoverTimer.current);
    }}
    onMouseLeave={() => {
      hidePopoverTimer.current = setTimeout(
        () => setPopoverPos(null),
        1000
      );
    }}
  >
  <PdfIcon />
  </IconButton>*/}
      </Popover>
      <Portal>
        <Snackbar
          open={snackbarInvalidOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarInvalidOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbarInvalidOpen(false)}
            variant="filled"
            severity="error"
          >
            Could not apply changes: exceeds maximum dimensions
          </Alert>
        </Snackbar>
      </Portal>
    </>
  );
};

export const ExportableComponent = ({
  id,
  labelIndex,
  children,
  showPopOver,
  x,
  y,
  width,
  height,
  laserExport,
  registerPathData,
  getPathData,
}: {
  id: string;
  labelIndex: number;
  x: number;
  y: number;
  width: number;
  height: number;
  laserExport: boolean;

  children: React.ReactElement<{
    exportMode?: boolean;
    // Export to PDF stuff
    registerPathData?: (id: string, p: Promise<string>) => void;
    getPathData?: (id: string) => string;
  }>;
  showPopOver: boolean;

  // Export to PDF stuff
  registerPathData?: (id: string, p: Promise<string>) => void;
  getPathData?: (id: string) => string;
}) => {
  const transformAttr = laserExport
    ? {}
    : { transform: `translate(${x}, ${y})`, fill: "white" };

  const visualComponent = (
    <g {...transformAttr} width={width} height={height}>
      {!laserExport && <rect width={width} height={height} fill="black"></rect>}
      {laserExport
        ? React.cloneElement(children, {
            exportMode: true,
            registerPathData,
            getPathData,
          })
        : children}
    </g>
  );

  return showPopOver ? (
    <LabelInteractions id={id} labelIndex={labelIndex}>
      {visualComponent}
    </LabelInteractions>
  ) : (
    visualComponent
  );
};

interface FiboIconsProps {
  label: Label;
  iconOffsetFromEdge: number;
  width: number;
  iconSize: number;
  strokeWidth: number;
  drawArrows: boolean;

  // Export to PDF stuff
  registerPathData?: (id: string, p: Promise<string>) => void;
  getPathData?: (id: string) => string;
}
const FiboIcons = ({
  label,
  iconSize,
  iconOffsetFromEdge,
  width,
  strokeWidth,
  drawArrows,
  registerPathData,
  getPathData,
}: FiboIconsProps) => {
  return (
    <g>
      {drawArrows && (
        <Icon
          size={iconSize}
          x={iconOffsetFromEdge}
          y={iconYOffset(iconSize)}
          id={"arrowUp"}
          strokeWidth={strokeWidth}
          registerPathData={registerPathData}
          getPathData={getPathData}
        />
      )}
      <Icon
        size={iconSize}
        x={iconOffsetFromEdge + iconSize * (drawArrows ? 1 : 0)}
        y={iconYOffset(iconSize)}
        id={label.leftIconId}
        strokeWidth={strokeWidth}
        registerPathData={registerPathData}
        getPathData={getPathData}
      />
      <Icon
        size={iconSize}
        x={width / 2 - iconSize / 2}
        y={iconYOffset(iconSize)}
        id={label.centerIconId}
        strokeWidth={strokeWidth}
        registerPathData={registerPathData}
        getPathData={getPathData}
      />
      <Icon
        size={iconSize}
        x={width - iconOffsetFromEdge - iconSize * (drawArrows ? 2 : 1)}
        y={iconYOffset(iconSize)}
        id={label.rightIconId}
        strokeWidth={strokeWidth}
        registerPathData={registerPathData}
        getPathData={getPathData}
      />
      {drawArrows && (
        <Icon
          size={iconSize}
          x={width - iconOffsetFromEdge - iconSize}
          y={iconYOffset(iconSize)}
          id={"arrowDown"}
          strokeWidth={strokeWidth}
          registerPathData={registerPathData}
          getPathData={getPathData}
        />
      )}
    </g>
  );
};

const hasArrows = (label: LabelOption) => {
  switch (label) {
    case LabelOption.ARROWS_CENTER_TEXT:
    case LabelOption.ARROWS_SIDE_TEXT:
    case LabelOption.ARROWS_SYMBOLS_NO_TEXT:
    case LabelOption.ARROWS_CENTER_SYMBOL:
      return true;
    default:
      return false;
  }
};

export interface LabelProps {
  label: Label;
  fontSize: number;
  textOffsetFromIcon: number;
  iconSize: number;
  strokeWidth: number;

  // Export to PDF stuff
  registerPathData?: (id: string, p: Promise<string>) => void;
  getPathData?: (id: string) => string;
}
export function BigLabel({
  label,
  fontSize,
  textOffsetFromIcon,
  iconSize,
  strokeWidth,
  registerPathData,
  getPathData,
}: LabelProps) {
  const iconOffsetFromEdge = 3.9;
  const drawArrows = hasArrows(label.labelType);

  return (
    <>
      <FiboIcons
        {...{
          iconOffsetFromEdge,
          iconSize,
          label,
          strokeWidth,
          drawArrows,
          getPathData,
          registerPathData,
          width: 47,
        }}
      />
      <g>
        <Text
          x={iconOffsetFromEdge + (drawArrows ? textOffsetFromIcon : 2)}
          y={3.3}
          fontSize={fontSize}
          textAnchor="start"
          registerPathData={registerPathData}
          getPathData={getPathData}
        >
          {label.leftText}
        </Text>
        <Text
          x={23.5}
          y={3.3}
          fontSize={fontSize}
          textAnchor="middle"
          registerPathData={registerPathData}
          getPathData={getPathData}
        >
          {label.centerText}
        </Text>
        <Text
          x={47 - (iconOffsetFromEdge + (drawArrows ? textOffsetFromIcon : 2))}
          y={3.3}
          fontSize={fontSize}
          textAnchor="end"
          registerPathData={registerPathData}
          getPathData={getPathData}
        >
          {label.rightText}
        </Text>
      </g>
    </>
  );
}

export function SmallLabel({
  label,
  fontSize,
  textOffsetFromIcon,
  iconSize,
  strokeWidth,
  registerPathData,
  getPathData,
}: LabelProps) {
  const iconOffsetFromEdge = 1;
  const drawArrows = hasArrows(label.labelType);

  return (
    <>
      <FiboIcons
        {...{
          iconOffsetFromEdge,
          iconSize,
          label,
          strokeWidth,
          drawArrows,
          getPathData,
          registerPathData,
          width: 31,
        }}
      />
      <g>
        <Text
          x={iconOffsetFromEdge + (drawArrows ? textOffsetFromIcon : 2)}
          y={3.3}
          fontSize={fontSize}
          textAnchor="start"
          registerPathData={registerPathData}
          getPathData={getPathData}
        >
          {label.leftText}
        </Text>
        <Text
          x={15.5}
          y={3.3}
          fontSize={fontSize}
          textAnchor="middle"
          registerPathData={registerPathData}
          getPathData={getPathData}
        >
          {label.centerText}
        </Text>
        <Text
          x={31 - (iconOffsetFromEdge + (drawArrows ? textOffsetFromIcon : 2))}
          y={3.3}
          fontSize={fontSize}
          textAnchor="end"
          registerPathData={registerPathData}
          getPathData={getPathData}
        >
          {label.rightText}
        </Text>
      </g>
    </>
  );
}

interface FiboProps {
  id: string;
  label: Label[];
  finish: Finish;
  textOffsetFromIcon: number;
  fontSize: number;
  iconSize: number;
  strokeWidth: number;
  exportMode: boolean;

  registerPathData?: (id: string, p: Promise<string>) => void;
  getPathData?: (id: string) => string;
}

export function FiboTwoWay({
  id,
  label,
  finish,
  textOffsetFromIcon,
  fontSize,
  iconSize,
  strokeWidth,
  exportMode,
  registerPathData,
  getPathData,
}: FiboProps) {
  const labelComponent = (
    <BigLabel
      label={label[0]}
      textOffsetFromIcon={textOffsetFromIcon}
      fontSize={fontSize}
      iconSize={iconSize}
      strokeWidth={strokeWidth}
      registerPathData={registerPathData}
      getPathData={getPathData}
    />
  );
  return (
    <>
      {exportMode ? (
        <g stroke="black" fill="none" strokeWidth="0.2">
          <rect x="0" y="0" width="80" height="120"></rect>
          <rect x="0" y="0" width="31.5" height="120"></rect>
          <rect x="33" y="0" width="47" height="65.5"></rect>
          <rect x="33" y="72.5" width="47" height="47.5"></rect>
          <rect x="31.5" y="0" width="1.5" height="120"></rect>
        </g>
      ) : (
        <image
          href={`/assets/images/devices/0272/${finish
            .toString()
            .padStart(2, "0")}.png`}
          height="100%"
          width="100%"
          preserveAspectRatio="none"
        />
      )}
      <ExportableComponent
        id={id}
        labelIndex={0}
        showPopOver={!exportMode}
        x={33}
        y={65.5}
        width={47}
        height={7}
        laserExport={false}
      >
        {labelComponent}
      </ExportableComponent>
    </>
  );
}

export function FiboFourWay({
  id,
  label,
  finish,
  textOffsetFromIcon,
  fontSize,
  iconSize,
  strokeWidth,
  exportMode,
  registerPathData,
  getPathData,
}: FiboProps) {
  return (
    <>
      {exportMode ? (
        <g stroke="black" fill="none" strokeWidth="0.2">
          <rect x="0" y="0" width="79.75" height="120"></rect>
          <rect x="0" y="0" width="47.25" height="47.25"></rect>
          <rect x="0" y="54" width="47.25" height="66"></rect>
          <rect x="48.75" y="0" width="31" height="81.3"></rect>
          <rect x="48.75" y="88.25" width="31" height="31.75"></rect>

          <rect x="47.25" y="0" width="1.5" height="120"></rect>
        </g>
      ) : (
        <image
          href={`/assets/images/devices/0274/${finish
            ?.toString()
            .padStart(2, "0")}.png`}
          height="100%"
          width="100%"
          preserveAspectRatio="none"
        />
      )}
      <ExportableComponent
        id={id}
        labelIndex={0}
        showPopOver={!exportMode}
        x={0}
        y={47.25}
        width={47.25}
        height={7}
        laserExport={false}
      >
        <BigLabel
          label={label[0]}
          textOffsetFromIcon={textOffsetFromIcon}
          fontSize={fontSize}
          iconSize={iconSize}
          strokeWidth={strokeWidth}
          registerPathData={registerPathData}
          getPathData={getPathData}
        />
      </ExportableComponent>
      <ExportableComponent
        id={id}
        labelIndex={1}
        showPopOver={!exportMode}
        x={48.75}
        y={81.3}
        width={31}
        height={7}
        laserExport={false}
      >
        <SmallLabel
          label={label[1]}
          textOffsetFromIcon={textOffsetFromIcon}
          fontSize={fontSize}
          iconSize={iconSize}
          strokeWidth={strokeWidth}
          registerPathData={registerPathData}
          getPathData={getPathData}
        />
      </ExportableComponent>
    </>
  );
}

function Preview() {
  const classes = useStyles();
  const [
    highQualityRender,
    setHighQualityRender,
  ] = useState<React.ReactElement | null>(null);

  const fibo = useSelector((state: RootState) =>
    fiboSelectors.selectById(state, state.uiState.activeId)
  );
  const advanced = useSelector((state: RootState) => state.document.advanced);

  const getFiboComponent = useCallback(() => {
    switch (fibo?.type) {
      case FiboType.Quad:
        return (
          <FiboFourWay
            id={fibo.id}
            label={fibo.label}
            finish={fibo.finish}
            exportMode={false}
            {...advanced}
          />
        );
      case FiboType.Dual:
        return (
          <FiboTwoWay
            id={fibo.id}
            label={fibo.label}
            finish={fibo.finish}
            exportMode={false}
            {...advanced}
          />
        );
    }
  }, [advanced, fibo]);

  useEffect(() => {
    setHighQualityRender(null);
    const component = getFiboComponent();

    const subscription = component
      ? from(getRenderedComponent(component, false)).subscribe((res) =>
          setHighQualityRender(res as React.ReactElement)
        )
      : undefined;

    // Make sure we don't set an 'old' value when this effect is fired multiple times in quick succession
    return () => subscription?.unsubscribe();
  }, [getFiboComponent, setHighQualityRender]);

  if (!fibo) return <></>;

  return (
    <Box
      position="relative"
      display="flex"
      pt={4}
      px={4}
      textAlign="center"
      justifyContent="center"
    >
      <svg className={classes.svg} viewBox="0 0 80 120">
        {highQualityRender ? highQualityRender : getFiboComponent()}
      </svg>
      {!highQualityRender && (
        <Box
          position="absolute"
          top="55px"
          left="0"
          width="100%"
          display="flex"
          justifyContent="center"
        >
          <Paper>
            <Box px={2} py={1} display="flex" alignItems="center">
              <CircularProgress size={20} />
              <Box ml={2}>
                <Typography variant="button">Loading...</Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
}

export default Preview;
